// ============================================================================
// ui/heading-image.js — the cover's headline, rendered in the buyer's own face
// ============================================================================
// Ryan 2026-08-27: *"on customer sheet, make the main heading which is their name
// or CUSTOM WEIGHT DESIGN[S] in the font they chose on their weights."*
//
// ⛔ THE WORKER CANNOT DO THIS, AND NOT FOR WANT OF EFFORT. `pdf-lib` embeds TTF
// and OTF; every face in this library ships as **woff2**, whose glyph table is
// **brotli**-compressed, and a Cloudflare Worker's `DecompressionStream` supports
// gzip and deflate only. So there is no path from the bytes we have to a font the
// PDF generator can embed — not a missing dependency, a missing decompressor.
//
// The BROWSER already has every face: `styles/fonts.css` declares all 137 for the
// 2D DOM. So the heading is rasterised here, where the font is, and travels as a
// picture. That is also why it needs no new drawing code on the other side —
// `generateCoverPage` has taken an image for the title slot since Project Assembly
// added `logoAsTitle` for *"customers whose logo IS their wordmark"*, and a
// wordmark is exactly what this is.
//
// ⚠ THE COLOUR IS BAKED IN, so it has to match. `#000F1C` is `PDF_NAVY` in
// `rfq-worker/src/pdf/constants.js` and the app's own brand ink — one value, two
// repos, no import between them. If the cover's navy ever moves, this moves with
// it or the headline stops matching the rest of the page.
// ============================================================================

import manifest from '../fonts/manifest.js';

/** `PDF_NAVY` — see the header. Keep in step with `rfq-worker/src/pdf/constants.js`. */
const INK = '#000F1C';

/** Rendered at 4× the size it prints at, so the title is crisp on paper, not just on screen. */
const RENDER_PX = 220;
/** The cover fits the title into 340×96pt; past this width the type is unreadable anyway. */
const MAX_W = 3600;

const FACE = new Map((Array.isArray(manifest) ? manifest : []).map((f) => [f.id, f]));

/**
 * Draw `text` in the design's typeface onto a transparent PNG.
 *
 * Best-effort and silent: a null return simply means the cover falls back to setting the heading in
 * Helvetica, which is what it did before this existed. A document must never fail over a nicety.
 *
 * @returns {Promise<string|null>} bare base64 PNG, or null
 */
export async function renderHeadingPng(text, fontId) {
  const words = String(text || '').trim();
  if (!words) return null;
  const face = FACE.get(String(fontId || ''));
  if (!face || !face.css) return null;

  const weight = face.weight || '400';
  const family = `"${face.css}"`;
  const spec = `${weight} ${RENDER_PX}px ${family}`;

  /* ⛔ LOAD THE FACE FIRST, AND WAIT FOR IT. `@font-face` is lazy: the browser fetches a face when
     something on the page needs it, and a canvas asking for a family it has not fetched gets the
     FALLBACK, silently and at a different width. The plate on screen may be wearing this face
     already — or may not, if the buyer picked it and the sheet is generated from a reopened design.
     This is the same race `fonts/fonts.js` documents for the 3D texture, one layer up. */
  try {
    if (document.fonts && document.fonts.load) {
      await document.fonts.load(spec, words);
      // `load` resolves per-face; `ready` covers a face still settling from an earlier request.
      if (document.fonts.ready) await document.fonts.ready;
    }
  } catch { /* an unavailable face renders as the fallback below, which is still a heading */ }

  try {
    const measure = document.createElement('canvas').getContext('2d');
    measure.font = spec;
    const caps = words.toUpperCase();          // the cover sets its title in caps
    const m = measure.measureText(caps);
    const w = Math.ceil(m.width);
    if (!w) return null;

    /* Ascent + descent from the real metrics rather than a multiple of the size: these faces run
       from a condensed gothic to a slab with a deep descender, and a fixed box either clips the
       tail or floats the type in empty space the cover then scales down to fit. */
    const asc = Math.ceil(m.actualBoundingBoxAscent || RENDER_PX * 0.75);
    const desc = Math.ceil(m.actualBoundingBoxDescent || RENDER_PX * 0.25);
    const pad = Math.round(RENDER_PX * 0.08);

    const scale = Math.min(1, MAX_W / (w + pad * 2));
    const cv = document.createElement('canvas');
    cv.width = Math.max(1, Math.round((w + pad * 2) * scale));
    cv.height = Math.max(1, Math.round((asc + desc + pad * 2) * scale));

    const g = cv.getContext('2d');
    g.scale(scale, scale);
    g.font = spec;
    g.textBaseline = 'alphabetic';
    g.fillStyle = INK;
    g.fillText(caps, pad, pad + asc);

    const blob = await new Promise((res) => cv.toBlob(res, 'image/png'));
    if (!blob) return null;
    return await new Promise((res) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result || '').replace(/^data:[^,]*,/, ''));
      fr.onerror = () => res(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
